// Symbol.for so two malachite copies in one realm share one sheet per id, as the `<style>`
// path got for free from getElementById.
const REGISTRY = Symbol.for('malachite.styleSheets');

type Registry = WeakMap<Document, Map<string, CSSStyleSheet>>;

// Per document: a constructed sheet cannot be adopted into a different one.
const cacheFor = (doc: Document): Map<string, CSSStyleSheet> => {
    const global = globalThis as unknown as Record<symbol, Registry | undefined>;
    const registry = global[REGISTRY] ?? new WeakMap();
    global[REGISTRY] = registry;
    let cache = registry.get(doc);
    if (!cache) {
        cache = new Map();
        registry.set(doc, cache);
    }
    return cache;
};

const warned = new Set<string>();

const warnBlocked = (id: string): void => {
    if (warned.has(id)) {
        return;
    }
    warned.add(id);
    console.warn(
        `[malachite] Stylesheet "${id}" did not apply. A host Content-Security-Policy is ` +
            'blocking <style> elements, and this browser is too old for the adoptedStyleSheets ' +
            'fallback (needs Firefox 101+ / Safari 16.4+). Overlay stacking and option ' +
            'highlights will not paint.',
    );
};

/**
 * Installs `rules` once per `id` and returns the sheet. Adopts a constructed stylesheet where
 * possible, because a host CSP without `style-src 'unsafe-inline'` blocks a `<style>` outright.
 * `rules` is an array because these cascades depend on source order. `@import` is unsupported.
 */
export const injectStyleSheet = (id: string, rules: readonly string[]): CSSStyleSheet | null => {
    if (typeof document === 'undefined') {
        return null;
    }
    const css = rules.join('\n');

    // Probed, not try/caught: jsdom lets the assignment succeed and then ignores the sheet.
    const adopted = document.adoptedStyleSheets as CSSStyleSheet[] | undefined;
    if (adopted && typeof CSSStyleSheet !== 'undefined') {
        try {
            const cache = cacheFor(document);
            let sheet = cache.get(id);
            if (!sheet) {
                sheet = new CSSStyleSheet();
                sheet.replaceSync(css);
                cache.set(id, sheet);
            }
            // Re-checked, never latched: a host reassigning the array silently drops ours.
            if (!adopted.includes(sheet)) {
                document.adoptedStyleSheets = [...adopted, sheet];
            }
            return sheet;
        } catch {
            // Cross-document sheet or a failed construction — fall through to the element.
        }
    }

    const existing = document.getElementById(id) as HTMLStyleElement | null;
    if (existing) {
        return existing.sheet;
    }
    if (!document.head) {
        return null;
    }
    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
    if (!style.sheet) {
        warnBlocked(id);
    }
    return style.sheet;
};
